// Layer 3 (Transformations): the Maintenance landing's rows — the eight
// equipment categories, the work list and the reliability improvement register
// (map-role-dashboards.md §7; polish.P14 #701; #734 one work-order set). MTBF
// facts are Paul's structure; the work list is `/maintenance`'s own PM schedule
// (`pm.seed.ts` through `PmSchedule.transform.ts`), never a second typed set.
// `rpn` is carried but never rendered (map §7a note). The landing's projections
// over these rows live in `MaintenanceLanding.transform.ts`.

import type { SessionState } from '@/versions/v1/schema/scenario';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import { FAILURE_MODES } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { READINGS } from '@/versions/v1/shared/bunker-hill-readings';
import { productionDayOf } from '@/versions/v1/shared/clock.derive';
import { valueAt } from '@/versions/v1/shared/series.derive';
import {
  bm01BearingTrend,
  bm01BearingTrendPhrase,
  type BM01BearingTrendFit,
} from '@/versions/v1/modules/downtime/ValueLost.transform';
import { pmTaskRows, pmTasks, type PmTaskRow } from '@/versions/v1/modules/maintenance/PmSchedule.transform';
import { formatDateOffsetFromCursor } from './LandingCursor.transform';

/** A run of copy; `strong` marks the figure the reader scans for (600 ink), `code` an id set in mono. */
export type TextSegment = { text: string; strong?: boolean; code?: boolean };

export type EquipmentHealthRow = {
  id: string;
  name: string;
  /** The plan-view name the insight line uses (`Concentrate pumps`). */
  plainName: string;
  /** The plant area, set in small caps above the name on the ledger. */
  area: string;
  cond: 'ok' | 'warn' | 'bad';
  /** `unmeasured` where the row's own series cannot be fitted into a trend at this cursor. */
  trend: 'worsening' | 'improving' | 'stable' | 'unmeasured';
  trendLabel: string;
  trendClassName: string;
  mtbfActual: number;
  mtbfTarget: number;
  mtbfCaption: string | null;
  rpn: number;
  note: string;
  /** The condition read now, its figure strong. */
  now: readonly TextSegment[];
  /** The line under it: the projection or the fix that clears it. */
  nowSub?: string;
  /** An active alert on the category today: its label and the short name the strip lists. */
  alert?: { label: string; short: string };
  /** What happens next on this category, with the step's own clock where it has
   *  one (a passed clock greys the step, never a `late` state). The step is a
   *  condition-monitoring action, not a scheduled work order: the landing's work
   *  orders are `/maintenance`'s PM schedule and none of them names these
   *  categories (#734). */
  next?: { text: string; at?: SeriesTime };
  /** Failures in the year to date, where the category counts them, and what one is called. */
  failures?: { ytd: number; noun: string };
};

function equipmentTrendDisplay(trend: EquipmentHealthRow['trend']): { label: string; className: string } {
  if (trend === 'worsening') return { label: 'down — worsening', className: 'text-[var(--state-blocked)]' };
  if (trend === 'improving') return { label: 'up — improving', className: 'text-[var(--state-online)]' };
  if (trend === 'unmeasured') return { label: 'unmeasured', className: 'text-muted' };
  return { label: 'stable', className: 'text-[var(--state-warning)]' };
}

function equipmentHealthRow(
  row: Omit<EquipmentHealthRow, 'trendLabel' | 'trendClassName' | 'mtbfCaption'>,
  trendLabelOverride?: string,
): EquipmentHealthRow {
  const trend = equipmentTrendDisplay(row.trend);
  return {
    ...row,
    trendLabel: trendLabelOverride ?? trend.label,
    trendClassName: trend.className,
    mtbfCaption: row.mtbfActual > 0 ? `MTBF ${row.mtbfActual}w vs ${row.mtbfTarget}w target` : null,
  };
}

/** The shared BM01 restart trend, or null when the series cannot fit one at
 *  this cursor (before the restart, or fewer than two post-settle samples):
 *  the row then reads the bearing at the cursor against the limit it has
 *  crossed, with the trend `unmeasured`. */
function bearingTrendAt(state: SessionState): BM01BearingTrendFit | null {
  const trend = bm01BearingTrend(state);
  return trend.state === 'fitted' ? trend : null;
}

/** The cursor's production day and a clock on it — the ledger's next steps are
 *  condition actions on the shift the reader is in. */
function todayAt(state: SessionState, hm: string): SeriesTime {
  return `${productionDayOf(state.cursor).date}T${hm}` as SeriesTime;
}

// [syn] Paul's §7a condition facts, one constant each so the ledger line and the note agree.
const PH_DRIFT = 0.3;
const SEAL_FAILURES_YTD = 11;
const SPIGOT_WEAR_PCT = 11;
const SPIGOT_THRESHOLD_PCT = 15;
const LIME_SILO_PCT = 26;
const LIME_DELIVERY_HM = '21:00';
const VFD_TRIPS_YTD = 9;
const TROMMEL_BLINDING_YTD = 3;

export function equipmentHealthRows(state: SessionState): EquipmentHealthRow[] {
  const bearingTrend = bearingTrendAt(state);
  const bearingFmea = FAILURE_MODES.find((row) => row.id === 'fm-trunnion');
  const bearingReading = READINGS.find((row) => row.id === 'rd-bm-brg-temp');
  const bearingNow = bearingTrend?.currentValue ?? valueAt('rd-bm-brg-temp', state) ?? bearingReading?.value ?? null;
  const warnC = bearingReading?.band?.hi;
  const limitC = bearingReading?.spec?.hi;
  const bearingHealth = bearingTrend === null
    ? { trend: 'unmeasured' as const, label: 'unmeasured' }
    : bearingTrend.direction === 'rising'
      ? { trend: 'worsening' as const, label: 'rising — worsening' }
      : bearingTrend.direction === 'falling'
        ? { trend: 'improving' as const, label: 'falling · improving' }
        : { trend: 'stable' as const, label: 'steady' };
  const bearingPhrase = bm01BearingTrendPhrase(bm01BearingTrend(state));
  const bearingText = bearingNow === null ? null : `${bearingNow.toFixed(1)}°C`;
  const bearingOverWarn = bearingNow !== null && warnC !== undefined && bearingNow > warnC;
  // The condition reads its own limit once the reading is over it; under the
  // limit the row keeps the warning-band read it has always carried.
  const bearingOverLimit = limitC !== undefined && bearingNow !== null && bearingNow > limitC;

  return [
    equipmentHealthRow({
      id: 'eq-1',
      name: 'Ball mill BM01',
      plainName: 'Ball mill BM01',
      area: 'Grinding',
      cond: bearingOverLimit ? 'bad' : 'warn',
      trend: bearingHealth.trend,
      mtbfActual: 6.5,
      mtbfTarget: 24,
      rpn: bearingFmea?.rpn ?? 0,
      note: [
        `TE-622-625 bearing temp ${bearingText ?? '—'} vs ${warnC ?? '—'}°C warning / ${limitC ?? '—'}°C limit`,
        bearingPhrase ? ` · ${bearingPhrase}` : '',
        '. Vibration check due 14:00.',
      ].join(''),
      now: [
        { text: 'Bearing ' },
        ...(bearingNow === null ? [] : [{ text: `${bearingNow.toFixed(1)} °C`, strong: true }]),
        ...(bearingOverLimit
          ? [{ text: ` vs ${limitC} °C limit` }]
          : warnC === undefined
            ? []
            : [{ text: ` vs ${warnC} °C warning` }]),
      ],
      ...(bearingPhrase ? { nowSub: bearingPhrase } : {}),
      ...(bearingOverWarn ? { alert: { label: 'Tier 1', short: 'BM01 bearing' } } : {}),
      next: { text: 'Vibration check 14:00', at: todayAt(state, '14:00') },
    }, bearingHealth.label),
    equipmentHealthRow({
      id: 'eq-2',
      name: 'Cyclone cluster (8 units)',
      plainName: 'Cyclone cluster',
      area: 'Grinding',
      cond: 'warn',
      trend: 'improving',
      mtbfActual: 10.1,
      mtbfTarget: 20,
      rpn: 180,
      note: `Unit 4 spigot at ${SPIGOT_WEAR_PCT}% wear — threshold ${SPIGOT_THRESHOLD_PCT}%. Next measure ${formatDateOffsetFromCursor(state, 10)}.`,
      now: [{ text: 'Unit 4 spigot ' }, { text: `${SPIGOT_WEAR_PCT}%`, strong: true }, { text: ` wear vs ${SPIGOT_THRESHOLD_PCT}% threshold` }],
      next: { text: `Measure ${formatDateOffsetFromCursor(state, 10)}` },
    }),
    equipmentHealthRow({
      id: 'eq-3',
      name: 'Con pumps P-RC01/02',
      plainName: 'Concentrate pumps',
      area: 'Concentrate',
      cond: 'bad',
      trend: 'worsening',
      mtbfActual: 3.1,
      mtbfTarget: 12,
      rpn: 160,
      note: `${SEAL_FAILURES_YTD} events YTD — mechanical seal failures. VSD upgrade IMP-005 resolves root cause.`,
      now: [{ text: `${SEAL_FAILURES_YTD}`, strong: true }, { text: ' seal failures YTD' }],
      nowSub: 'VSD upgrade IMP-005 resolves root cause',
      next: { text: 'Seal inspection 10:00', at: todayAt(state, '10:00') },
      failures: { ytd: SEAL_FAILURES_YTD, noun: 'seal failures' },
    }),
    equipmentHealthRow({
      id: 'eq-4',
      name: 'pH probes AT-128/150/155',
      plainName: 'pH probes',
      area: 'Reagents',
      cond: 'warn',
      trend: 'worsening',
      mtbfActual: 4.4,
      mtbfTarget: 8,
      rpn: 90,
      note: `AT-150 calibration due today — drift ${PH_DRIFT} pH detected.`,
      now: [{ text: 'AT-150 drift ' }, { text: `${PH_DRIFT} pH`, strong: true }],
      alert: { label: 'Alert', short: 'AT-150 calibration' },
      next: { text: 'Calibration in progress' },
    }),
    equipmentHealthRow({
      id: 'eq-5',
      name: 'Lime silo and dosing',
      plainName: 'Lime silo and dosing',
      area: 'Reagents',
      cond: 'warn',
      trend: 'stable',
      mtbfActual: 5.3,
      mtbfTarget: 16,
      rpn: 112,
      note: `Silo at ${LIME_SILO_PCT}% — delivery ${LIME_DELIVERY_HM}. Doser running correctly.`,
      now: [{ text: 'Silo ' }, { text: `${LIME_SILO_PCT}%`, strong: true }, { text: ' · doser running' }],
      next: { text: `Delivery ${LIME_DELIVERY_HM}`, at: todayAt(state, LIME_DELIVERY_HM) },
    }),
    equipmentHealthRow({
      id: 'eq-6',
      name: 'VFD drives CV-01/02',
      plainName: 'VFD drives',
      area: 'Feed',
      cond: 'ok',
      trend: 'improving',
      mtbfActual: 4.3,
      mtbfTarget: 26,
      rpn: 140,
      note: `${VFD_TRIPS_YTD} trips YTD. Parameter review completed — trending improved.`,
      now: [{ text: `${VFD_TRIPS_YTD}`, strong: true }, { text: ' trips YTD · parameter review done' }],
      failures: { ytd: VFD_TRIPS_YTD, noun: 'trips' },
    }),
    equipmentHealthRow({
      id: 'eq-7',
      name: 'SAG trommel SM01',
      plainName: 'SAG trommel',
      area: 'Grinding',
      cond: 'ok',
      trend: 'improving',
      mtbfActual: 7.8,
      mtbfTarget: 16,
      rpn: 108,
      note: `${TROMMEL_BLINDING_YTD} blinding events YTD. Larger trommel IMP-009 approved.`,
      now: [{ text: `${TROMMEL_BLINDING_YTD}`, strong: true }, { text: ' blinding events YTD' }],
      next: { text: 'IMP-009 approved' },
      failures: { ytd: TROMMEL_BLINDING_YTD, noun: 'blinding events' },
    }),
    equipmentHealthRow({
      id: 'eq-8',
      name: 'Flotation cells Pb and Zn',
      plainName: 'Flotation cells',
      area: 'Flotation',
      cond: 'ok',
      trend: 'stable',
      mtbfActual: 0,
      mtbfTarget: 0,
      rpn: 72,
      note: `All agitators running. Next rotor inspection ${formatDateOffsetFromCursor(state, 14)}.`,
      now: [{ text: 'All agitators running' }],
      next: { text: `Rotor inspection ${formatDateOffsetFromCursor(state, 14)}` },
    }),
  ];
}

/**
 * The landing's work list: `/maintenance`'s own open PM schedule, read through
 * `pmTaskRows()` over `pm.seed.ts` (#734, one work-order set). The landing keeps
 * no typed work orders of its own — a row here is the same instance, with the
 * same asset, due date and estimated hours, that the PM schedule tab draws. The
 * rows stay in that transform's order (overdue first, then due date); the
 * landing only decodes a due tone and a status tone on top.
 */
export function maintScheduleRows(state: SessionState): PmTaskRow[] {
  return pmTaskRows(pmTasks(), state.cursor);
}

export type CapitalRegisterRow = {
  id: string;
  title: string;
  /** What the change does, with its figures. */
  businessCase: string;
  status: 'Investigation' | 'Approved';
  /** The equipment category the change addresses. */
  categoryId: string;
  /** Approved rows carry their payback; the rest go to the GM as a business case. */
  payback?: string;
};

// [syn] Paul's §7c register facts.
const IMP005_UNPLANNED_HOURS_YTD = 42;
const IMP003_DEPLETIONS_YTD = 7;
const IMP003_DISRUPTION_HOURS = 2.5;

export function capitalRegisterRows(state: SessionState): CapitalRegisterRow[] {
  return [
    {
      id: 'IMP-005',
      title: 'VSD upgrade — concentrate pumps',
      businessCase: `Eliminates mechanical seal failures (${IMP005_UNPLANNED_HOURS_YTD} h unplanned YTD)`,
      status: 'Investigation',
      categoryId: 'eq-3',
    },
    {
      id: 'IMP-003',
      title: 'Lime slaker upgrade',
      businessCase: `${IMP003_DEPLETIONS_YTD} lime depletion events YTD · ${IMP003_DISRUPTION_HOURS} h process disruption each · removes manual lime handling`,
      status: 'Investigation',
      categoryId: 'eq-5',
    },
    {
      id: 'IMP-004',
      title: 'Ceramic cyclone spigots',
      businessCase: `Spigot life 10 → 22 weeks · procurement on order, delivery ${formatDateOffsetFromCursor(state, 20)}`,
      status: 'Approved',
      categoryId: 'eq-2',
      payback: '0.8 yr payback',
    },
  ];
}

/** Sorted worst-gap first, filtered to `mtbfActual > 0` — map §7c / §10. Names print in full. */
export function mtbfChartRows(state: SessionState): { id: string; name: string; actual: number; target: number; ratio: number }[] {
  return equipmentHealthRows(state)
    .filter((row) => row.mtbfActual > 0)
    .map((row) => ({
      id: row.id,
      name: row.name,
      actual: row.mtbfActual,
      target: row.mtbfTarget,
      ratio: row.mtbfTarget > 0 ? row.mtbfActual / row.mtbfTarget : 1,
    }))
    .sort((a, b) => a.ratio - b.ratio);
}

export function mtbfBarTone(ratio: number): 'ok' | 'warn' | 'bad' {
  if (ratio >= 0.8) return 'ok';
  if (ratio >= 0.5) return 'warn';
  return 'bad';
}
